import { useCallback, useEffect, useState } from 'react'
import type { LifeProfile, Milestone } from '../types/life'

const PROFILE_KEY = 'memento.profile.v1'
const MILESTONES_KEY = 'memento.milestones.v1'

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* приватный режим — просто живём в памяти */
  }
}

export interface LifeStore {
  profile: LifeProfile | null
  milestones: Milestone[]
  saveProfile: (profile: LifeProfile) => void
  upsertMilestone: (milestone: Milestone) => void
  removeMilestone: (id: string) => void
}

export function useLifeStore(): LifeStore {
  const [profile, setProfile] = useState<LifeProfile | null>(() => read<LifeProfile | null>(PROFILE_KEY, null))
  const [milestones, setMilestones] = useState<Milestone[]>(() => read<Milestone[]>(MILESTONES_KEY, []))

  useEffect(() => {
    if (profile) write(PROFILE_KEY, profile)
  }, [profile])

  useEffect(() => {
    write(MILESTONES_KEY, milestones)
  }, [milestones])

  const saveProfile = useCallback((next: LifeProfile) => setProfile(next), [])

  const upsertMilestone = useCallback((next: Milestone) => {
    setMilestones((prev) => {
      const exists = prev.some((m) => m.id === next.id)
      return exists ? prev.map((m) => (m.id === next.id ? next : m)) : [...prev, next]
    })
  }, [])

  const removeMilestone = useCallback((id: string) => {
    setMilestones((prev) => prev.filter((m) => m.id !== id))
  }, [])

  return { profile, milestones, saveProfile, upsertMilestone, removeMilestone }
}
